import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import ErrorMessageComponent from "../../../components/ErrorMessageComponent";
import { useFirebaseDeliveryOrders } from "../../../hooks/useFirebaseOrders";
import {
  AssignedCart,
  DeliveryOrder,
  Group,
  GroupOrder,
} from "../../../redux/deliveryRouteOptimizer";
import {
  AssignCartToTentativeOrderRequest,
  useAssignCartToTentativeOrderMutation,
  useCreateGroupMutation,
  useCreateGroupOrderMutation,
  useDeleteTentativeGroupOrderMutation,
  useGetOrderListMutation,
  useLazyGetTentativeGroupOrdersQuery,
  useUpdateGroupOrderStatusMutation,
  useRemoveOrderFromGroupOrderMutation,
} from "../../../redux/services/orderService";
import { useLazyGetCartsForRestaurantByRestaurantIdQuery } from "../../../redux/services/restaurantServices";
import ActionBar from "./Actionbar";
import DeliveryPanels from "./DeliveryPanel";
import GroupModal from "./GroupModal";
import "./index.css";
import GoogleMapView from "./MapView";
import { PREPARING_ORDER_STATUS } from "../../../constants/constans";

const DeliveryRouteOptimizer: React.FC = () => {
  const [getOrderList, { data: apiData, error }] = useGetOrderListMutation();

  const [createGroupMutation, { isLoading: isCreatingGroup }] =
    useCreateGroupMutation();

  const [fetchGroupOrders, { data: groupOrdersData }] =
    useLazyGetTentativeGroupOrdersQuery();

  const [fetchCarts, { data: cartsData }] =
    useLazyGetCartsForRestaurantByRestaurantIdQuery();

  const [assignCartToTentativeOrder] = useAssignCartToTentativeOrderMutation();

  const [createGroupOrder, { isLoading: isCreatingActualGroups }] =
    useCreateGroupOrderMutation();

  const [deleteTentativeGroupOrder] = useDeleteTentativeGroupOrderMutation();

  const [updateGroupOrderStatus, { isLoading: isUpdatingGroupStatus }] =
    useUpdateGroupOrderStatusMutation();

  const [removingOrderId, setRemovingOrderId] = useState<string | null>(null);
  const [removeOrderFromGroupOrder, { isLoading: isRemovingOrder }] =
    useRemoveOrderFromGroupOrderMutation();

  const [deliveries, setDeliveries] = useState<DeliveryOrder[]>([]);
  const [vehicles, setVehicles] = useState<AssignedCart[]>([]);
  const [selectedGroupsForMap, setSelectedGroupsForMap] = useState<string[]>(
    [],
  );
  const [showOnlySelectedGroups, setShowOnlySelectedGroups] = useState(false);
  const [routes, setRoutes] = useState<Record<string, string[]>>({});
  const [groups, setGroups] = useState<Record<string, Group>>({});
  const [selectedDeliveries, setSelectedDeliveries] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<"map" | "list">("map");
  const [hoveredDelivery, setHoveredDelivery] = useState<string | null>(null);
  const [selectedVehicleFilter, setSelectedVehicleFilter] = useState<string[]>(
    [],
  );
  const [showAllVehicles, setShowAllVehicles] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [workflowStep, setWorkflowStep] = useState<
    "group" | "assign" | "actual"
  >("group");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroupsForActual, setSelectedGroupsForActual] = useState<
    string[]
  >([]);

  const [assigningGroupId, setAssigningGroupId] = useState<string | null>(null);
  const [isAssigningCartLocal, setIsAssigningCartLocal] = useState(false);
  const [selectedCartsForMap, setSelectedCartsForMap] = useState<string[]>([]);
  const [showOnlySelectedCarts, setShowOnlySelectedCarts] = useState(false);
  const [isRefreshingLeftPanel, setIsRefreshingLeftPanel] = useState(false);

  const [groupTypeFilter, setGroupTypeFilter] = useState<
    "tentative" | "finalized"
  >("tentative");
  const [tentativeFilter, setTentativeFilter] = useState<
    "all" | "assigned" | "unassigned"
  >("all");

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loadedItems, setLoadedItems] = useState({
    orders: false,
    groups: false,
    carts: false,
  });

  const [selectedOrdersForMap, setSelectedOrdersForMap] = useState<string[]>(
    [],
  );
  const [showOnlySelectedOrders, setShowOnlySelectedOrders] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeMapFilter, setActiveMapFilter] = useState<
    "none" | "group" | "orders"
  >("none");

  const [updatingGroupId, setUpdatingGroupId] = useState<string | null>(null);

  const groupColors = (index: number): string => {
    const colors = ["#ff0000"];
    return colors[index % colors.length];
  };
  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id,
  );

  // The REST "getorderlist" endpoint used below does not return
  // orderNote / packingNote / paymentNote. Pull those three fields
  // straight from Firestore so we can merge them in ourselves.
  const todayForNotes = new Date();
  const notesRangeStart = new Date(
    todayForNotes.getFullYear(),
    todayForNotes.getMonth(),
    todayForNotes.getDate(),
    0, 0, 0,
  );
  const notesRangeEnd = new Date(
    todayForNotes.getFullYear(),
    todayForNotes.getMonth(),
    todayForNotes.getDate(),
    23, 59, 59,
  );

  const { data: firestoreOrdersForNotes } = useFirebaseDeliveryOrders(
    restaurantId,
    notesRangeStart,
    notesRangeEnd,
  );

  const notesByOrderId = React.useMemo(() => {
      const map = new Map<string, { orderNote?: string; packingNote?: string; paymentNote?: string }>();
      firestoreOrdersForNotes?.orders?.forEach((o: any) => {
      map.set(o.id, {
        orderNote: o.orderNote,
        packingNote: o.packingNote,
        paymentNote: o.paymentNote,
      });
    });
    return map;
  }, [firestoreOrdersForNotes]);

  // Reset all filters when switching between workflow steps (Orders, Group, Vehicle tabs)
  useEffect(() => {
    // Reset all map filters when workflow step changes
    setSelectedOrdersForMap([]);
    setShowOnlySelectedOrders(false);
    setSelectedCartsForMap([]);
    setShowOnlySelectedCarts(false);
    setSelectedGroupsForMap([]);
    setShowOnlySelectedGroups(false);
    setActiveMapFilter("none");

    // Reset other filter states
    setSelectedVehicleFilter([]);
    setShowAllVehicles(false);
  }, [workflowStep]);

  // Reset specific filters when switching between group types in assign tab
  useEffect(() => {
    if (workflowStep === "assign") {
      setSelectedCartsForMap([]);
      setShowOnlySelectedCarts(false);
      setSelectedGroupsForMap([]);
      setShowOnlySelectedGroups(false);
    }
  }, [workflowStep, groupTypeFilter]);

  // Track loaded items
  useEffect(() => {
    if (apiData) setLoadedItems((prev) => ({ ...prev, orders: true }));
  }, [apiData]);

  useEffect(() => {
    if (groupOrdersData) setLoadedItems((prev) => ({ ...prev, groups: true }));
  }, [groupOrdersData]);

  useEffect(() => {
    if (cartsData) setLoadedItems((prev) => ({ ...prev, carts: true }));
  }, [cartsData]);

  // Check if all data is loaded
  const allDataLoaded =
    loadedItems.orders && loadedItems.groups && loadedItems.carts;

  // Reset initial load after all data is fetched
  useEffect(() => {
    if (allDataLoaded) {
      setIsInitialLoad(false);
    }
  }, [allDataLoaded]);

  const toggleGroupSelectionForActual = (groupId: string) => {
    setSelectedGroupsForActual((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  };

  // Select/deselect all groups
  const toggleSelectAllGroupsForActual = () => {
    const assignableGroups = Object.values(groups).filter(
      (group) => !group.isCollectionGrouped && group.cart?.cartId,
    );

    if (selectedGroupsForActual.length === assignableGroups.length) {
      setSelectedGroupsForActual([]);
    } else {
      setSelectedGroupsForActual(assignableGroups.map((group) => group.id));
    }
  };

  const mainCartId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.deliveryCartId,
  );

  // Fetch orders and groups in parallel
  useEffect(() => {
   // Replace your current date calculation logic
const fetchAllData = async () => {
  if (!restaurantId) return;

  try {
    const today = new Date();
    
    // Get local date components
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    
    // Use local date in the format YYYY-MM-DD
    const currentDate = `${year}-${month}-${day}`;
    
    const startDate = `${currentDate}T00:00:00`;
    const endDate = `${currentDate}T23:59:59`;

    await Promise.all([
      getOrderList({
        restaurantId: restaurantId,
        startDate: startDate,
        endDate: endDate,
        isDeliveryOrder: true,
        orderStatus: "All",
        onlyApproved: true,
        cartId: mainCartId || undefined,
      }).unwrap(),
      fetchGroupOrders({ restaurantId }).unwrap(),
      fetchCarts(restaurantId).unwrap(),
    ]);
  } catch (err) {
    // Error handling
  }
};

    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getOrderList, fetchGroupOrders, fetchCarts, restaurantId]);

  // Transform API data to Delivery format and handle groups
  useEffect(() => {
    if (apiData?.items && groupOrdersData && cartsData) {
      // Create a map of all orders for easy lookup
      const ordersMap = new Map<string, DeliveryOrder>();
      apiData.items.forEach((order: DeliveryOrder) => {
        ordersMap.set(order.id, order);
      });

      // Create a Set of all order IDs that are in groups
      const groupedOrderIds = new Set<string>();
      const newGroups: Record<string, Group> = {};

      // Process group orders and create deliveries for grouped orders
      const groupedDeliveries: DeliveryOrder[] = [];

      if (groupOrdersData) {
        groupOrdersData.forEach((groupOrder: GroupOrder, index) => {
          const groupId = groupOrder.id;
          const color = groupColors(index);

          // Store all order IDs in this group
          const validOrderIds: string[] = [];

          // Create delivery objects for each order in the group
          groupOrder.orderIds.forEach((orderId) => {
            groupedOrderIds.add(orderId);
            const order = ordersMap.get(orderId);

            if (order) {
              const delivery = transformOrderToDelivery(order, groupId);
              if (delivery) {
                groupedDeliveries.push(delivery);
                validOrderIds.push(orderId);
              }
            } else {
            }
          });

          // Determine cart information
          let cartInfo = undefined;
          if (groupOrder.cart) {
            const deliveryCartId = groupOrder.cart.cartId;

            cartInfo = {
              cartId: mainCartId,
              deliveryCartId: deliveryCartId, 
              displayName: groupOrder.cart.displayName, 
              cartPersonId: groupOrder.cart.cartPersonId,
              userId: groupOrder.cart.userId,
              cartPersonName: groupOrder.cart.cartPersonName,
            } as any; 
          }

          // Create group object with isCollectionGrouped property
          newGroups[groupId] = {
            id: groupId,
            name: groupOrder.groupOrderName,
            color: color,
            deliveryIds: validOrderIds,
            cart: cartInfo,
            isCollectionGrouped: groupOrder.isCollectionGrouped || false,
            deliveryRoute: groupOrder.deliveryRoute,
            finalizedGroupOrderId: groupOrder.finalizedGroupOrderId,
            finalizedGroupOrderStatus: groupOrder.finalizedGroupOrderStatus,
          };
        });

        setGroups(newGroups);
      }

      // Transform regular orders to deliveries (excluding those in groups)
      const ungroupedDeliveries: DeliveryOrder[] = apiData.items
        .filter((order: DeliveryOrder) => {
          // Only include orders that are NOT in any group
          const isInGroup = groupedOrderIds.has(order.id);
          const isPreparing = order.orderStatus === PREPARING_ORDER_STATUS;
          return !isInGroup && !isPreparing;
        })
        .map((order: DeliveryOrder) => transformOrderToDelivery(order))
        .filter((delivery): delivery is DeliveryOrder => delivery !== null);

      // Combine both ungrouped and grouped deliveries
      const allDeliveries = [...ungroupedDeliveries, ...groupedDeliveries];

      // Filter out deliveries without coordinates
      const validDeliveries = allDeliveries.filter((d) => d.lat && d.lng);

      setDeliveries(validDeliveries);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData, groupOrdersData, cartsData, notesByOrderId]);

  // Helper function to transform API order to Delivery
  const transformOrderToDelivery = (
    order: DeliveryOrder,
    groupId: string | null = null,
  ): DeliveryOrder | null => {
    // Map order status to delivery status
    const getDeliveryStatus = (
      orderStatus: string,
    ): DeliveryOrder["status"] => {
      switch (orderStatus) {
        case "Pending":
        case "ApprovedByRestaurant":
          return groupId ? "grouped" : "unassigned";
        case "AssignedToDriver":
          return "assigned";
        default:
          return groupId ? "grouped" : "unassigned";
      }
    };

    // Check if coordinates exist
    const hasCoordinates =
      order.deliveryDetails?.coordinates?.latitude &&
      order.deliveryDetails?.coordinates?.longitude;

    if (!hasCoordinates) {
      return null;
    }

    return {
      id: order.id,
      status: getDeliveryStatus(order.orderStatus),
      orderStatus: order.orderStatus,
      organization: order.organization,
      groupId: groupId as any,
      vehicleId: null,
      orderReference: order.orderReference,
      totalPrice: order.totalPrice,
      paymentMethod: order.paymentMethod,
      phoneNumber: order.phoneNumber,
      displayName: order.displayName,
      customer: order.customer || order.organization,
      orderItems: order.orderItems,
      deliveryDetails: order.deliveryDetails,
      paymentSummary: order.paymentSummary,
      createdAt: order.createdAt,
      deliverAt: order.deliverAt,
      isDeliveryOrder: order.isDeliveryOrder,
      isDirectRestaurantOrder: order.isDirectRestaurantOrder,
      isScheduledOrder: order.isScheduledOrder,
      paymentCompleted: order.paymentCompleted,
      lat: order.deliveryDetails.coordinates.latitude,
      lng: order.deliveryDetails.coordinates.longitude,
      address: order.deliveryDetails.address || "",
      groupedForCollection: order.groupedForCollection || false,
      orderNote: notesByOrderId.get(order.id)?.orderNote ?? order.orderNote,
      packingNote: notesByOrderId.get(order.id)?.packingNote ?? order.packingNote,
      paymentNote: notesByOrderId.get(order.id)?.paymentNote ?? order.paymentNote,
    };
  };

  useEffect(() => {
    if (cartsData && mainCartId) {
      // Find the restaurant's main cart (Curry King HQ)
      const mainCart = cartsData.find((cart: any) => cart.id === mainCartId);

      // Get the delivery cart IDs from the main cart
      const deliveryCartIds = mainCart?.deliveryCartsIds || [];

      // Filter carts to show only delivery carts that are associated with the main cart
      const deliveryCarts = cartsData.filter(
        (cart: any) =>
          deliveryCartIds.includes(cart.id) && !cart.isFixedLocation,
      );

      const transformedVehicles: AssignedCart[] = deliveryCarts.map(
        (cart: any, index: number) => {
          const vehicleColors = (index: number): string => {
            const colors = ["#ff0000"];
            return colors[index % colors.length];
          };
          const color = vehicleColors(index);

          return {
            id: cart.id,
            name: cart.displayName || "",
            maxDistance: cart.loadingConfigurations?.distance || 50,
            color: color,
            isFixedLocation: cart.isFixedLocation,
            cartState: cart.cartState,
            location: cart.location,
            liveLocation: cart.liveLocation,
            owner: cart.owner,
            loadingConfigurations: cart.loadingConfigurations,
            // Store the main cart ID for reference
            mainCartId: mainCartId,
          };
        },
      );

      setVehicles(transformedVehicles);

      // Also store the main cart for reference
      if (mainCart) {
        setMainCart(mainCart);
      }
    }
  }, [cartsData, mainCartId]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mainCart, setMainCart] = useState<any>(null);

  // Initialize routes from groups with carts
  useEffect(() => {
    if (groups && vehicles.length > 0) {
      const newRoutes: Record<string, string[]> = {};

      // Initialize routes from groups that have carts assigned
      Object.values(groups).forEach((group) => {
        if (group.cart?.cartId && group.deliveryIds.length > 0) {
          const cartId = group.cart.cartId;
          const vehicleExists = vehicles.some((v) => v.id === cartId);
          if (vehicleExists) {
            newRoutes[cartId] = [
              ...(newRoutes[cartId] || []),
              ...group.deliveryIds,
            ];
          }
        }
      });

      setRoutes((prevRoutes) => ({
        ...prevRoutes,
        ...newRoutes,
      }));
    }
  }, [groups, vehicles]);

  useEffect(() => {
    const cleanupOrphanedRoutes = () => {
      const updatedRoutes = { ...routes };
      let hasChanges = false;

      Object.keys(updatedRoutes).forEach((vehicleId) => {
        const routeDeliveries = updatedRoutes[vehicleId] || [];

        // Check if this vehicle is assigned to any group
        const isAssignedToGroup = Object.values(groups).some(
          (group) => group.cart?.cartId === vehicleId,
        );

        if (!isAssignedToGroup && routeDeliveries.length === 0) {
          delete updatedRoutes[vehicleId];
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setRoutes(updatedRoutes);
      }
    };

    if (Object.keys(groups).length > 0) {
      cleanupOrphanedRoutes();
    }
  }, [groups, routes]);

  const toggleDeliverySelection = (deliveryId: string) => {
    setSelectedDeliveries((prev) =>
      prev.includes(deliveryId)
        ? prev.filter((id) => id !== deliveryId)
        : [...prev, deliveryId],
    );
  };

  const canCreateGroup =
    selectedDeliveries.length > 0 &&
    selectedDeliveries.every((deliveryId) => {
      const delivery = deliveries.find((d) => d.id === deliveryId);
      return delivery && delivery.status === "unassigned" && !delivery.groupId;
    });

  const addToGroup = async (groupId: string, groupName?: string) => {
    if (selectedDeliveries.length === 0) return;

    try {
      const isNewGroup = !groups[groupId];
      let actualGroupId = groupId;

      if (isNewGroup && groupName) {
        // This is for creating a completely new group
        const payload = {
          restaurantId: restaurantId,
          groupOrderName: groupName,
          orderIds: selectedDeliveries,
        };

        const response = await createGroupMutation(payload).unwrap();
        actualGroupId =
          response?.groupOrderId || response?.id || Date.now().toString();

        // Create the new group in local state
        const color = groupColors(Object.keys(groups).length);
        const newGroup: Group = {
          id: actualGroupId,
          name: groupName,
          color: color,
          deliveryIds: selectedDeliveries,
          isCollectionGrouped: false,
          finalizedGroupOrderId: response?.finalizedGroupOrderId,
          finalizedGroupOrderStatus: response?.finalizedGroupOrderStatus,
        };

        // Update deliveries status
        const updatedDeliveries = deliveries.map((d) =>
          selectedDeliveries.includes(d.id)
            ? {
                ...d,
                status: "grouped" as const,
                groupId: actualGroupId as any,
              }
            : d,
        );

        setGroups((prev) => ({ ...prev, [actualGroupId]: newGroup }));
        setDeliveries(updatedDeliveries);
      } else {
        // This is for adding to an existing group
        const group = groups[groupId];

        const allOrderIds = [
          ...new Set([...group.deliveryIds, ...selectedDeliveries]),
        ];

        // Prepare payload with groupOrderId for existing groups
        const payload = {
          restaurantId: restaurantId,
          groupOrderName: group.name,
          orderIds: allOrderIds,
          groupOrderId: groupId,
        };

        const response = await createGroupMutation(payload).unwrap();
        actualGroupId = response?.groupOrderId || response?.id || groupId;

        // Update local state
        const updatedGroups = { ...groups };

        // Remove selected deliveries from other groups
        Object.keys(updatedGroups).forEach((gId) => {
          if (gId !== actualGroupId) {
            updatedGroups[gId].deliveryIds = updatedGroups[
              gId
            ].deliveryIds.filter((id) => !selectedDeliveries.includes(id));
          }
        });

        // Handle case where API returns a different group ID
        if (actualGroupId !== groupId) {
          updatedGroups[actualGroupId] = {
            ...updatedGroups[groupId],
            id: actualGroupId,
            deliveryIds: allOrderIds,
            finalizedGroupOrderId: updatedGroups[groupId].finalizedGroupOrderId,
            finalizedGroupOrderStatus:
              updatedGroups[groupId].finalizedGroupOrderStatus,
          };
          // Remove the old group if it's empty
          if (updatedGroups[groupId].deliveryIds.length === 0) {
            delete updatedGroups[groupId];
          }
        } else {
          // Update the existing group with new order IDs
          updatedGroups[actualGroupId].deliveryIds = allOrderIds;
        }

        // Update deliveries to reflect new group assignments
        const updatedDeliveries = deliveries.map((d) =>
          selectedDeliveries.includes(d.id)
            ? {
                ...d,
                status: "grouped" as const,
                groupId: actualGroupId as any,
              }
            : d,
        );

        setGroups(updatedGroups);
        setDeliveries(updatedDeliveries);
      }

      toast.success(
        isNewGroup
          ? "Group created successfully"
          : "Orders added to group successfully",
      );
      setSelectedDeliveries([]);
      setNewGroupName("");
      setShowGroupModal(false);

      // Refresh groups data
      fetchGroupOrders({ restaurantId }).unwrap();
    } catch (err) {
      toast.error(
        `Failed to ${
          !groups[groupId] ? "create" : "update"
        } group. Please try again.`,
      );
    }
  };

  const removeCartFromGroup = async (groupId: string) => {
    const group = groups[groupId];
    if (!group) return;

    const previousDeliveryCartId = group.cart?.deliveryCartId;

    const result = await Swal.fire({
      title: "Remove Cart Assignment?",
      text: `Are you sure you want to remove the cart assignment from group "${group.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, remove it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "cart-remove-confirmation",
        confirmButton: "cart-remove-confirm-btn",
        cancelButton: "cart-remove-cancel-btn",
      },
      buttonsStyling: true,
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          if (!mainCartId) {
            Swal.showValidationMessage("Main cart not found");
            return false;
          }

          // Call the API with null deliveryCartId
          const payload: AssignCartToTentativeOrderRequest = {
            restaurantId: restaurantId,
            groupOrderId: groupId,
            cartId: mainCartId, 
            deliveryCartId: null, 
          };

          await assignCartToTentativeOrder(payload).unwrap();

          // Update local state after successful removal
          const updatedGroups = { ...groups };
          if (updatedGroups[groupId]) {
            updatedGroups[groupId].cart = undefined;
          }

          // Update deliveries status
          const updatedDeliveries = deliveries.map((d) =>
            group.deliveryIds.includes(d.id)
              ? {
                  ...d,
                  status: "grouped" as const,
                  vehicleId: null,
                  cartId: null,
                }
              : d,
          );

          // Update routes - remove deliveries from the vehicle's route
          const updatedRoutes = { ...routes };
          if (previousDeliveryCartId && updatedRoutes[previousDeliveryCartId]) {
            updatedRoutes[previousDeliveryCartId] = updatedRoutes[
              previousDeliveryCartId
            ].filter((deliveryId) => !group.deliveryIds.includes(deliveryId));

            // Remove empty vehicle routes
            if (updatedRoutes[previousDeliveryCartId].length === 0) {
              delete updatedRoutes[previousDeliveryCartId];
            }
          }

          // Update ALL state in a single batch
          setGroups(updatedGroups);
          setDeliveries(updatedDeliveries);
          setRoutes(updatedRoutes);

          // Force remove from selected carts
          if (
            previousDeliveryCartId &&
            selectedCartsForMap.includes(previousDeliveryCartId)
          ) {
            setSelectedCartsForMap((prev) =>
              prev.filter((id) => id !== previousDeliveryCartId),
            );
          }

          return true;
        } catch (error) {
          Swal.showValidationMessage(`Request failed: ${error}`);
          return false;
        }
      },
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: "Removed!",
        text: `Cart assignment has been removed from group "${group.name}".`,
        icon: "success",
        confirmButtonColor: "#10b981",
        timer: 2000,
        timerProgressBar: true,
      });

      // Refresh groups data to ensure consistency
      fetchGroupOrders({ restaurantId }).unwrap();
    }
  };

  const handleCreateGroupFromModal = (groupName: string) => {
    // Use a temporary ID that will be replaced by the actual API response
    const tempGroupId = `new-${Date.now()}`;
    addToGroup(tempGroupId, groupName);
  };

  const deleteGroup = async (groupId: string) => {
    const group = groups[groupId];
    const result = await Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "group-delete-confirmation",
        confirmButton: "group-delete-confirm-btn",
        cancelButton: "group-delete-cancel-btn",
      },
      buttonsStyling: true,
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const result = await deleteTentativeGroupOrder({
            restaurantId: restaurantId,
            groupOrderId: groupId,
          }).unwrap();

          // Update local state to remove the group
          const groupDeliveryIds = groups[groupId].deliveryIds;

          const updatedDeliveries = deliveries.map((d) =>
            groupDeliveryIds.includes(d.id)
              ? {
                  ...d,
                  status: "unassigned" as const,
                  groupId: null,
                  vehicleId: null,
                }
              : d,
          );

          const updatedGroups = { ...groups };
          delete updatedGroups[groupId];

          const updatedRoutes = { ...routes };
          Object.keys(updatedRoutes).forEach((vId) => {
            updatedRoutes[vId] = updatedRoutes[vId].filter(
              (dId) => !groupDeliveryIds.includes(dId),
            );
          });

          setGroups(updatedGroups);
          setDeliveries(updatedDeliveries);
          setRoutes(updatedRoutes);

          // Refresh groups data
          fetchGroupOrders({ restaurantId }).unwrap();

          return true;
        } catch (error) {
          Swal.showValidationMessage(`Request failed: ${error}`);
          return false;
        }
      },
    });

    if (result.isConfirmed) {
      // Show success message
      Swal.fire({
        title: "Deleted!",
        text: `Group "${group.name}" has been deleted successfully.`,
        icon: "success",
        confirmButtonColor: "#10b981",
        timer: 2000,
        timerProgressBar: true,
      });
    }
  };

  const onSaveGroupChanges = async (groupId: string, orderIds: string[]) => {
    try {
      const group = groups[groupId];

      // Prepare payload for API call
      const payload = {
        restaurantId: restaurantId,
        groupOrderName: group.name,
        groupOrderId: groupId,
        orderIds: orderIds,
        ...(group.deliveryIds.length > 0 && { groupOrderId: groupId }),
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const response = await createGroupMutation(payload).unwrap();
      toast.success("Group updated successfully");

      // Update local state to reflect the changes
      const updatedGroups = { ...groups };
      updatedGroups[groupId].deliveryIds = orderIds;

      // Update delivery statuses for removed orders
      const removedOrderIds = group.deliveryIds.filter(
        (id) => !orderIds.includes(id),
      );
      const updatedDeliveries = deliveries.map((d) =>
        removedOrderIds.includes(d.id)
          ? {
              ...d,
              status: "unassigned" as const,
              groupId: null,
              vehicleId: null,
            }
          : d,
      );

      setGroups(updatedGroups);
      setDeliveries(updatedDeliveries);
    } catch (err) {
      toast.error("Failed to update group. Please try again.");
    }
  };

 const assignVehicleToGroup = async (groupId: string, deliveryCartId: string) => {
  const group = groups[groupId];
  if (!group || group.deliveryIds.length === 0) return;

  setAssigningGroupId(groupId);
  setIsAssigningCartLocal(true);

  try {
    if (!mainCartId) {
      toast.error("Main cart (Curry King HQ) not found");
      return;
    }

    const payload: AssignCartToTentativeOrderRequest = {
      restaurantId: restaurantId,
      groupOrderId: groupId,
      cartId: mainCartId, 
      deliveryCartId: deliveryCartId, 
    };


    await assignCartToTentativeOrder(payload).unwrap();

    toast.success(`Delivery cart assigned to group "${group.name}" successfully`);

    // Find the vehicle/delivery cart details
    const assignedVehicle = vehicles.find((v) => v.id === deliveryCartId);

    // Find the previously assigned delivery cart ID (if any)
    const previousDeliveryCartId = (group.cart as any)?.deliveryCartId;

    // Update local state after successful API call
    const updatedDeliveries = deliveries.map((d) =>
      group.deliveryIds.includes(d.id)
        ? { 
            ...d, 
            status: "assigned" as const, 
            vehicleId: deliveryCartId as any,
            cartId: mainCartId,
          }
        : d,
    );

    // Update the group with new cart information
    const updatedGroups = { ...groups };
    updatedGroups[groupId] = {
      ...updatedGroups[groupId],
      cart: assignedVehicle
        ? {
            cartId: mainCartId, 
            deliveryCartId: assignedVehicle.id, 
            displayName: assignedVehicle.name,
            cartPersonId: (group.cart as any)?.cartPersonId,
            userId: (group.cart as any)?.userId,
            cartPersonName: (group.cart as any)?.cartPersonName,
          } as any 
        : undefined,
    };

    // Update routes
    const updatedRoutes = { ...routes };

    // Remove deliveries from previous delivery cart's route
    if (previousDeliveryCartId && updatedRoutes[previousDeliveryCartId]) {
      updatedRoutes[previousDeliveryCartId] = updatedRoutes[
        previousDeliveryCartId
      ].filter((deliveryId) => !group.deliveryIds.includes(deliveryId));

      if (updatedRoutes[previousDeliveryCartId].length === 0) {
        delete updatedRoutes[previousDeliveryCartId];
      }
    }

    // Add deliveries to new delivery cart's route
    updatedRoutes[deliveryCartId] = [
      ...(updatedRoutes[deliveryCartId] || []).filter(
        (deliveryId) => !group.deliveryIds.includes(deliveryId),
      ),
      ...group.deliveryIds,
    ];

    setRoutes(updatedRoutes);
    setDeliveries(updatedDeliveries);
    setGroups(updatedGroups);
    
    // console.log("Updated group cart info:", updatedGroups[groupId].cart);
    
  } catch (error: any) {
    console.error("Failed to assign cart:", error);
    toast.error("Failed to assign delivery cart. Please try again.");
  } finally {
    setAssigningGroupId(null);
    setIsAssigningCartLocal(false);
  }
};

    const restaurantLocation = useSelector(
      (state: any) => state.restaurant.selectedRestaurantDetails.location
    );

const handleUpdateGroupOrderStatus = async (
  groupId: string,
  newStatus: string,
) => {
  const group = groups[groupId];
  if (!group || !group.finalizedGroupOrderId) {
    toast.error("Cannot update status: Group is not finalized");
    return;
  }

  setUpdatingGroupId(groupId);

  try {

    // Prepare live location object
    const liveLocation = {
      latitude: restaurantLocation?.latitude  || 6.5224261, // Fallback to provided values
      longitude: restaurantLocation?.longitude || 70.857911 // Fallback to provided values
    };

    const payload = {
      restaurantId: restaurantId,
      groupOrderId: group.finalizedGroupOrderId,
      groupOrderStatus: newStatus,
      // Add liveLocation only for Dispatched or Completed status
      ...((newStatus === "Dispatched" || newStatus === "Completed") && {
        liveLocation: liveLocation
      })
    };

    // console.log("Update status payload:", payload); // For debugging

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const response = await updateGroupOrderStatus(payload).unwrap();

    // Update local state immediately
    const updatedGroups = { ...groups };
    updatedGroups[groupId] = {
      ...updatedGroups[groupId],
      finalizedGroupOrderStatus: newStatus,
    };

    setGroups(updatedGroups);
    toast.success(`Group status updated to ${newStatus}`);

    // Refresh groups data to ensure consistency
    fetchGroupOrders({ restaurantId }).unwrap();
  } catch (error: any) {
    console.error("Failed to update group order status:", error);

    // Direct extraction from RTK Query error structure
    let errorMessage = "Failed to update group status. Please try again.";

    // The actual API response is in error.data
    if (error.data) {
      if (typeof error.data === "string") {
        errorMessage = error.data;
      } else if (error.data.message) {
        errorMessage = error.data.message;
      } else if (error.data.error) {
        errorMessage = error.data.error;
      }
    }

    // If no specific message in data, use status-based messages
    if (
      errorMessage === "Failed to update group status. Please try again." &&
      error.status
    ) {
      switch (error.status) {
        case 400:
          errorMessage = "Invalid request data";
          break;
        case 404:
          errorMessage = "Group order not found";
          break;
        case 500:
          errorMessage = "Server error occurred";
          break;
      }
    }

    toast.error(errorMessage);
  } finally {
    setUpdatingGroupId(null);
  }
};

const handleRemoveOrderFromGroup = async (groupId: string, orderId: string) => {
  const group = groups[groupId];
  if (!group || !group.finalizedGroupOrderId) {
    toast.error("Cannot remove order: Group is not finalized");
    return;
  }

  setRemovingOrderId(orderId);

  try {
    const response = await removeOrderFromGroupOrder({
      restaurantId,
      groupOrderId: group.finalizedGroupOrderId,
      orderId,
    }).unwrap();

    // Optimistic local update — the refetch below will reconcile this
    setDeliveries((prev) =>
      prev.map((d) =>
        d.id === orderId
          ? { ...d, status: "unassigned" as const, groupId: null, vehicleId: null }
          : d,
      ),
    );

    setGroups((prev) => {
      const updated = { ...prev };
      if (updated[groupId]) {
        const remainingIds = updated[groupId].deliveryIds.filter(
          (id) => id !== orderId,
        );
        if (response?.data?.groupCancelled || remainingIds.length === 0) {
          delete updated[groupId];
        } else {
          updated[groupId] = { ...updated[groupId], deliveryIds: remainingIds };
        }
      }
      return updated;
    });

    setRoutes((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((vehicleId) => {
        updated[vehicleId] = updated[vehicleId].filter((id) => id !== orderId);
        if (updated[vehicleId].length === 0) delete updated[vehicleId];
      });
      return updated;
    });

    toast.success("Order removed from group successfully");

    // Refresh so tentative/finalized data (and the Orders tab) are authoritative
    fetchGroupOrders({ restaurantId }).unwrap();
  } catch (error: any) {
    console.error("Failed to remove order from group:", error);
    const message = error?.data?.message || error?.data;
    toast.error(
      typeof message === "string" ? message : "Failed to remove order from group. Please try again.",
    );
  } finally {
    setRemovingOrderId(null);
  }
};

  const getDeliveryGroup = (deliveryId: string): Group | undefined => {
    return Object.values(groups).find((g) =>
      g.deliveryIds.includes(deliveryId),
    );
  };

  const filteredDeliveries = React.useMemo(() => {
    if (!searchTerm.trim()) return deliveries;

    const searchLower = searchTerm.toLowerCase();
    return deliveries.filter(
      (delivery) =>
        delivery.orderReference?.toLowerCase().includes(searchLower) ||
        delivery.displayName?.toLowerCase().includes(searchLower) ||
        delivery.phoneNumber?.toLowerCase().includes(searchLower),
    );
  }, [deliveries, searchTerm]);

const handleCreateActualGroupOrders = async (selectedGroupIds?: string[]) => {
  // console.log("=== DEBUG: Creating actual group orders ===");
  
  try {
    if (!mainCartId) {
      toast.error("Main cart (Curry King HQ) not found");
      return;
    }

    // Get all non-collection groups
    const allRegularGroups = Object.values(groups).filter(
      (group) => !group.isCollectionGrouped
    );

    // console.log("All regular groups:", allRegularGroups.map(g => ({
    //   id: g.id,
    //   name: g.name,
    //   hasCart: !!g.cart,
    //   cartId: g.cart?.cartId,
    //   deliveryCartId: (g.cart as any)?.deliveryCartId, // This should now be populated
    //   displayName: g.cart?.displayName,
    //   deliveryIdsCount: g.deliveryIds.length
    // })));

    // If selectedGroupIds is provided, use only those groups
    const groupsToProcess = selectedGroupIds
      ? allRegularGroups.filter(
          (group) =>
            selectedGroupIds.includes(group.id) &&
            group.deliveryIds.length > 0
        )
      : allRegularGroups.filter(
          (group) =>
            group.deliveryIds.length > 0
        );

    // console.log("Groups to process:", groupsToProcess.length);
    // console.log("Groups to process details:", groupsToProcess.map(g => ({
    //   id: g.id,
    //   name: g.name,
    //   cartId: g.cart?.cartId,
    //   deliveryCartId: (g.cart as any)?.deliveryCartId,
    //   deliveryIds: g.deliveryIds
    // })));

    // Prepare payload with BOTH cartId (main) and deliveryCartId (if available)
    const payload = {
      groupOrders: groupsToProcess.map((group) => ({
        restaurantId: restaurantId,
        cartId: mainCartId, // Main cart ID (Curry King HQ) - always use the main cart
        deliveryCartId: (group.cart as any)?.deliveryCartId || null, // Delivery cart ID (can be null)
        orderIds: group.deliveryIds,
        tentativeGroupOrderId: group.id,
      })),
    };

    // console.log("API Payload:", payload);

    // Call the API
    const response = await createGroupOrder(payload).unwrap();

    // console.log("API Response:", response);

    if (response.message && response.message.includes("successfully")) {
      toast.success(
        `Actual group orders created successfully for ${groupsToProcess.length} group(s)!`,
      );

      // Clear selections and refresh
      setSelectedGroupsForMap([]);
      setSelectedCartsForMap([]);
      setSelectedOrdersForMap([]);
      setShowOnlySelectedGroups(false);
      setShowOnlySelectedCarts(false);
      setShowOnlySelectedOrders(false);
      setActiveMapFilter("none");

      // Clear group creation selections
      setSelectedDeliveries([]);

      // Refresh groups data
      fetchGroupOrders({ restaurantId }).unwrap();

      // Force a map refresh
      setViewMode("list");
      setTimeout(() => {
        setViewMode("map");
      }, 100);
    } else {
      toast.error(response.message || "Failed to create group orders");
    }
  } catch (error: any) {
    console.error("Error creating actual group orders:", error);
    
    // Check if error is about missing deliveryCartId
    if (error.data?.message?.includes("deliveryCartId")) {
      toast.error("Error: Missing delivery cart ID in some groups. Please reassign carts.");
    } else if (error.data?.message) {
      toast.error(`Failed: ${error.data.message}`);
    } else if (error.status) {
      toast.error(`Request failed with status: ${error.status}`);
    } else {
      toast.error("Failed to create actual group orders. Please try again.");
    }
  }
};
  // Get all deliveries including those in groups for map display
  const getAllDeliveriesForMap = (): DeliveryOrder[] => {
    return deliveries;
  };

  // Filter handler functions
  const handleGroupTypeFilterChange = (filter: "tentative" | "finalized") => {
    setGroupTypeFilter(filter);
  };

  const handleTentativeFilterChange = (
    filter: "all" | "assigned" | "unassigned",
  ) => {
    setTentativeFilter(filter);
  };

  const toggleOrderSelectionForMap = (orderId: string) => {
    setSelectedOrdersForMap((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId],
    );
    setActiveMapFilter("orders");
  };

  const toggleCartSelectionForMap = (cartId: string) => {
    setSelectedCartsForMap((prev) =>
      prev.includes(cartId)
        ? prev.filter((id) => id !== cartId)
        : [...prev, cartId],
    );
  };

  const toggleSelectAllCartsForMap = () => {
    const assignedVehicles = getAssignedVehicles();
    if (selectedCartsForMap.length === assignedVehicles.length) {
      setSelectedCartsForMap([]);
    } else {
      setSelectedCartsForMap(assignedVehicles.map((vehicle) => vehicle.id));
    }
  };

  const toggleGroupSelectionForMap = (groupId: string) => {
    setSelectedGroupsForMap((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  };

  const getAssignedVehicles = () => {
    return vehicles.filter((vehicle) => {
      const isAssignedToRegularGroup = Object.values(groups).some(
        (group) =>
          !group.isCollectionGrouped && group.cart?.cartId === vehicle.id,
      );

      const hasRegularGroupRoutes =
        routes[vehicle.id] &&
        routes[vehicle.id].length > 0 &&
        routes[vehicle.id].some((deliveryId) => {
          const delivery = deliveries.find((d) => d.id === deliveryId);
          if (!delivery || !delivery.groupId) return false;
          const group = groups[delivery.groupId];
          return group && !group.isCollectionGrouped;
        });

      return isAssignedToRegularGroup || hasRegularGroupRoutes;
    });
  };

  const toggleSelectAllOrdersForMap = () => {
    if (selectedOrdersForMap.length === filteredDeliveries.length) {
      setSelectedOrdersForMap([]);
      setActiveMapFilter("none");
    } else {
      setSelectedOrdersForMap(filteredDeliveries.map((d) => d.id));
      setActiveMapFilter("orders");
    }
  };

 const handleOptimizePath = async (
  groupId: string,
  orderIds: string[],
  isManuallyOptimized: boolean
) => {
  try {
    const group = groups[groupId];

    const payload: any = {
      restaurantId: restaurantId,
      groupOrderName: group.name,
      orderIds: orderIds,
      groupOrderId: groupId,
    };
    
    // Only add isManuallyOptimizedPath property if it's true
    if (isManuallyOptimized) {
      payload.isManuallyOptimizedPath = true;
    }
    
    const response = await createGroupMutation(payload).unwrap();
    const updatedGroups = { ...groups };
    updatedGroups[groupId] = {
      ...updatedGroups[groupId],
      deliveryIds: orderIds, 
      ...(isManuallyOptimized && { isManuallyOptimizedPath: true }),
      ...(response.finalizedGroupOrderId && { finalizedGroupOrderId: response.finalizedGroupOrderId }),
      ...(response.finalizedGroupOrderStatus && { finalizedGroupOrderStatus: response.finalizedGroupOrderStatus }),
    };

    setGroups(updatedGroups);
    toast.success(isManuallyOptimized ? "Changes saved successfully!" : "Path optimized successfully!");

    // Refresh groups data in background (optional - you might want to keep this for consistency)
    fetchGroupOrders({ restaurantId }).unwrap();
  } catch (err) {
    // If API call fails, revert the optimistic update
    // You might want to refetch the original data here
    toast.error("Failed to optimize path. Please try again.");
    // Refresh to get original state
    fetchGroupOrders({ restaurantId }).unwrap();
  }
};

 const handleRefreshLeftPanel = async () => {
  if (isRefreshingLeftPanel || !restaurantId) return;

  setIsRefreshingLeftPanel(true);

  try {
    const minimumLoadTime = new Promise((resolve) =>
      setTimeout(resolve, 1000),
    );

    const today = new Date();
    const currentDate = today.toISOString().split("T")[0];
    const startDate = `${currentDate}T00:00:00`;
    const endDate = `${currentDate}T23:59:59`;

    const fetchData = async () => {
      await Promise.all([
        fetchGroupOrders({ restaurantId }).refetch(),
        // For getOrderList, use trigger instead of unwrap
        getOrderList({
          restaurantId: restaurantId,
          startDate: startDate,
          endDate: endDate,
          isDeliveryOrder: true,
          orderStatus: "All",
          onlyApproved: true,
          cartId: mainCartId || undefined, 
        }),
      ]);
    };

    // Wait for both the data fetch and minimum load time
    await Promise.all([fetchData(), minimumLoadTime]);

    // No need to force re-render
  } catch (error) {
    console.error("Failed to refresh left panel:", error);
  } finally {
    setIsRefreshingLeftPanel(false);
  }
};

  // Show error state
  if (error) {
    return (
      <div className="delivery-route-optimizer">
        <ErrorMessageComponent />
      </div>
    );
  }

  return (
    <div className="delivery-route-optimizer">
      <div className="delivery-route-optimizer__container">
        <ActionBar workflowStep={workflowStep} />
        {showGroupModal && (
          <GroupModal
            newGroupName={newGroupName}
            setNewGroupName={setNewGroupName}
            setShowGroupModal={setShowGroupModal}
            selectedDeliveries={selectedDeliveries}
            isCreatingGroup={isCreatingGroup}
            onCreateGroup={handleCreateGroupFromModal}
          />
        )}

        <div className="delivery-route-optimizer__content-grid">
          {/* LEFT PANEL - Delivery Panels and other components */}
          <div className="delivery-route-optimizer__left-panel">
            <DeliveryPanels
              deliveries={deliveries}
              filteredDeliveries={filteredDeliveries}
              groups={groups}
              workflowStep={workflowStep}
              setWorkflowStep={setWorkflowStep}
              selectedDeliveries={selectedDeliveries}
              toggleDeliverySelection={toggleDeliverySelection}
              setHoveredDelivery={setHoveredDelivery}
              vehicles={vehicles}
              assignVehicleToGroup={assignVehicleToGroup}
              isAssigningCart={isAssigningCartLocal}
              assigningGroupId={assigningGroupId}
              addToGroup={addToGroup}
              setShowGroupModal={setShowGroupModal}
              canCreateGroup={canCreateGroup}
              deleteGroup={deleteGroup}
              onSaveGroupChanges={onSaveGroupChanges}
              onCreateActualGroupOrders={handleCreateActualGroupOrders}
              isCreatingActualGroups={isCreatingActualGroups}
              groupTypeFilter={groupTypeFilter}
              tentativeFilter={tentativeFilter}
              onGroupTypeFilterChange={handleGroupTypeFilterChange}
              onTentativeFilterChange={handleTentativeFilterChange}
              selectedOrdersForMap={selectedOrdersForMap}
              showOnlySelectedOrders={showOnlySelectedOrders}
              toggleOrderSelectionForMap={toggleOrderSelectionForMap}
              toggleSelectAllOrdersForMap={toggleSelectAllOrdersForMap}
              setShowOnlySelectedOrders={setShowOnlySelectedOrders}
              setActiveMapFilter={setActiveMapFilter}
              selectedCartsForMap={selectedCartsForMap}
              toggleCartSelectionForMap={toggleCartSelectionForMap}
              toggleSelectAllCartsForMap={toggleSelectAllCartsForMap}
              showOnlySelectedCarts={showOnlySelectedCarts}
              setShowOnlySelectedCarts={setShowOnlySelectedCarts}
              selectedGroupsForMap={selectedGroupsForMap}
              toggleGroupSelectionForMap={toggleGroupSelectionForMap}
              showOnlySelectedGroups={showOnlySelectedGroups}
              setShowOnlySelectedGroups={setShowOnlySelectedGroups}
              removeCartFromGroup={removeCartFromGroup}
              onOptimizePath={handleOptimizePath}
              onUpdateGroupOrderStatus={handleUpdateGroupOrderStatus}
              isUpdatingGroupStatus={isUpdatingGroupStatus}
              updatingGroupId={updatingGroupId}
              selectedGroupsForActual={selectedGroupsForActual}
              toggleGroupSelectionForActual={toggleGroupSelectionForActual}
              toggleSelectAllGroupsForActual={toggleSelectAllGroupsForActual}
              onRefreshLeftPanel={handleRefreshLeftPanel}
              isRefreshingLeftPanel={isRefreshingLeftPanel}
              onRemoveOrderFromGroup={handleRemoveOrderFromGroup}
              isRemovingOrder={isRemovingOrder}
              removingOrderId={removingOrderId}
            />
          </div>

          <div className="delivery-route-optimizer__right-panel">
            {viewMode === "map" ? (
              <GoogleMapView
                deliveries={getAllDeliveriesForMap()}
                vehicles={vehicles}
                routes={routes}
                groups={groups}
                selectedDeliveries={selectedDeliveries}
                hoveredDelivery={hoveredDelivery}
                setHoveredDelivery={setHoveredDelivery}
                toggleDeliverySelection={toggleDeliverySelection}
                workflowStep={workflowStep}
                showAllVehicles={showAllVehicles}
                selectedVehicleFilter={selectedVehicleFilter}
                getDeliveryGroup={getDeliveryGroup}
                groupTypeFilter={
                  workflowStep === "actual"
                    ? "tentative"
                    : workflowStep === "assign"
                      ? groupTypeFilter
                      : undefined
                }
                tentativeFilter={
                  workflowStep === "actual"
                    ? "assigned"
                    : workflowStep === "assign"
                      ? tentativeFilter
                      : undefined
                }
                selectedOrdersForMap={selectedOrdersForMap}
                showOnlySelectedOrders={showOnlySelectedOrders}
                selectedCartsForMap={selectedCartsForMap}
                showOnlySelectedCarts={showOnlySelectedCarts}
                selectedGroupsForMap={selectedGroupsForMap}
                showOnlySelectedGroups={showOnlySelectedGroups}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryRouteOptimizer;
